import React, { createContext, useContext, useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';

const CategoryManagementContext = createContext(null);

export const useCategory = () => {
  const context = useContext(CategoryManagementContext);
  if (!context) {
    throw new Error('useCategory must be used within a CategoryManagementProvider');
  }
  return context;
};

const DEFAULT_CATEGORIES = [
  { id: 'cat_1', name: 'Vencida', color: '#EF4444', daysThreshold: 0, description: 'Itens com prazo vencido' },
  { id: 'cat_2', name: 'Atenção', color: '#F97316', daysThreshold: 3, description: 'Vence em até 3 dias' },
  { id: 'cat_3', name: 'Próximo do vencimento', color: '#EAB308', daysThreshold: 7, description: 'Vence em até 7 dias' },
  { id: 'cat_4', name: 'Dentro do prazo', color: '#22C55E', daysThreshold: 999, description: 'Prazo confortável' }
];

export const CategoryManagementProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const { toast } = useToast();

  useEffect(() => {
    const stored = localStorage.getItem('app_categories');
    if (stored) {
      setCategories(JSON.parse(stored));
    } else {
      setCategories(DEFAULT_CATEGORIES);
      localStorage.setItem('app_categories', JSON.stringify(DEFAULT_CATEGORIES));
    }
  }, []);

  const saveCategories = (newCategories) => {
    setCategories(newCategories);
    localStorage.setItem('app_categories', JSON.stringify(newCategories));
  };

  const addCategory = (category) => {
    const newCat = { ...category, id: `cat_${Date.now()}` };
    const newCategories = [...categories, newCat];
    saveCategories(newCategories);
    toast({ title: 'Categoria adicionada', description: `${category.name} criada com sucesso.` });
  };

  const updateCategory = (id, updates) => {
    const newCategories = categories.map(c => c.id === id ? { ...c, ...updates } : c);
    saveCategories(newCategories);
    toast({ title: 'Categoria atualizada', description: 'Alterações salvas com sucesso.' });
  };

  const deleteCategory = (id) => {
    const newCategories = categories.filter(c => c.id !== id);
    saveCategories(newCategories);
    toast({ title: 'Categoria removida', description: 'A categoria foi excluída.' });
  };

  const getCategoryByDays = (days) => {
    if (days < 0) return categories.find(c => c.name === 'Vencida');
    
    // Find matching positive thresholds
    const sorted = [...categories]
      .filter(c => c.name !== 'Vencida' && c.daysThreshold > 0)
      .sort((a, b) => a.daysThreshold - b.daysThreshold);
      
    for (const cat of sorted) {
      if (days <= cat.daysThreshold) {
        return cat;
      }
    }
    return categories.find(c => c.name === 'Dentro do prazo') || sorted[sorted.length - 1];
  };

  return (
    <CategoryManagementContext.Provider value={{
      categories,
      addCategory,
      updateCategory,
      deleteCategory,
      getCategoryByDays
    }}>
      {children}
    </CategoryManagementContext.Provider>
  );
};